import React, { useEffect, useState } from 'react';
import './Orderdetail.css';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

function Orderdetail() {
    const { orderid } = useParams();
    const navigate = useNavigate();

    const [order, setOrder] = useState(null);
    const [images, setImages] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => { window.scrollTo(0, 0); }, []);

    useEffect(() => {
        if (!orderid) return;
        setLoading(true);
        fetch(`https://pentadeco.in/api/getorder/${orderid}`)
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setOrder(data.pro);
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [orderid]);

    // Fetch product images
    useEffect(() => {
        if (!order?.products?.length) return;
        const ids = [...new Set(order.products.map(p => p.id))];
        Promise.all(
            ids.map(proid =>
                fetch(`https://pentadeco.in/api/getproimg/${proid}`)
                    .then(r => r.json())
                    .then(d => ({ proid, image: d.success ? (d.image || d.images?.[0] || '/placeholder.png') : '/placeholder.png' }))
                    .catch(() => ({ proid, image: '/placeholder.png' }))
            )
        ).then(results => {
            const map = {};
            results.forEach(item => { map[item.proid] = item.image; });
            setImages(map);
        });
    }, [order]);

    const getStatusClass = (status) => {
        switch (status) {
            case 'pending':    return 'text-warning bg-warning-subtle';
            case 'confirmed':  return 'text-info bg-info-subtle';
            case 'packed':     return 'text-primary bg-primary-subtle';
            case 'dispatched': return 'text-secondary bg-secondary-subtle';
            case 'delivered':  return 'text-success bg-success-subtle';
            case 'cancelled':  return 'text-danger bg-danger-subtle';
            default:           return 'text-muted bg-light';
        }
    };

    const cancelOrder = async () => {
        const result = await Swal.fire({
            title: 'Cancel this order?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel it',
            cancelButtonText: 'No, keep it',
        });
        if (!result.isConfirmed) return;
        try {
            const res = await fetch(`https://pentadeco.in/api/orderstate/${orderid}/cancelled`);
            const data = await res.json();
            if (data.success) {
                Swal.fire('Cancelled!', 'Your order has been cancelled.', 'success');
                setOrder(prev => ({ ...prev, status: 'cancelled' }));
            } else {
                Swal.fire('Error', 'Failed to cancel the order.', 'error');
            }
        } catch {
            Swal.fire('Error', 'Something went wrong.', 'error');
        }
    };

    if (loading) {
        return (
            <div className="main-orderdetail">
                <div className="container py-5 text-center text-muted">Loading order details...</div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="main-orderdetail">
                <div className="container py-5 text-center">
                    <p className="text-muted body-font-medium">Order not found.</p>
                    <button className="btn btn-brand-secondary mt-3" onClick={() => navigate('/orders')}>Back to Orders</button>
                </div>
            </div>
        );
    }

    const rawTotal = order.products?.reduce((t, p) => t + p.price * p.quantity, 0) || 0;
    const discount = order.discount || 0;
    const finalTotal = rawTotal - discount;
    const canCancel = order.status !== 'delivered' && order.status !== 'cancelled';

    return (
        <div className="main-orderdetail">
            <div className="container py-4">

                {/* Back + header */}
                <div className="d-flex align-items-center mb-4">
                    <button className="btn btn-outline-brand-secondary btn-sm me-3 px-3" onClick={() => navigate('/orders')}>
                        <i className="fi fi-rr-arrow-left me-1" style={{ position: 'relative', bottom: '-2px' }}></i> Back
                    </button>
                    <h2 className="body-font-bold fs-4 mb-0">Order Details</h2>
                </div>

                <div className="row justify-content-center">
                    <div className="col-md-8 col-lg-7">

                        {/* Order ID + status */}
                        <div className="border rounded-brand p-3 mb-3 bg-white d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div>
                                <p className="mb-0 text-muted fs-sm">Order ID</p>
                                <p className="mb-0 body-font-semibold">{order.orderid}</p>
                                <p className="mb-0 text-muted fs-sm mt-1">{new Date(order.orderdate).toLocaleString()}</p>
                            </div>
                            <span className={`${getStatusClass(order.status)} rounded-pill px-3 py-1 body-font-bold text-capitalize`} style={{ fontSize: '0.8rem' }}>
                                {order.status}
                            </span>
                        </div>

                        {/* Products */}
                        <div className="border rounded-brand p-3 mb-3 bg-white">
                            <h5 className="body-font-semibold mb-3 fs-6">Products</h5>
                            {order.products?.map((p, i) => (
                                <div key={i} className={`d-flex align-items-center gap-3 pb-2 mb-2 ${i < order.products.length - 1 ? 'border-bottom' : ''}`}>
                                    <img
                                        src={images[p.id] || '/placeholder.png'}
                                        alt={p.proname}
                                        className="rounded-brand-sm flex-shrink-0"
                                        style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                    />
                                    <div className="flex-grow-1">
                                        <p className="mb-0 body-font-medium lh-sm">{p.proname}</p>
                                        <p className="mb-0 text-muted fs-sm">Qty: {p.quantity} &times; ₹{p.price}</p>
                                    </div>
                                    <p className="mb-0 body-font-semibold flex-shrink-0">₹{p.price * p.quantity}</p>
                                </div>
                            ))}

                            {/* Price summary */}
                            <div className="mt-2 pt-2 border-top">
                                {discount > 0 && (
                                    <>
                                        <div className="d-flex justify-content-between text-muted fs-sm mb-1">
                                            <span>Subtotal</span>
                                            <span>₹{rawTotal}</span>
                                        </div>
                                        <div className="d-flex justify-content-between text-success fs-sm mb-1">
                                            <span>Coupon ({order.coupon})</span>
                                            <span>-₹{discount}</span>
                                        </div>
                                    </>
                                )}
                                <div className="d-flex justify-content-between pt-2 border-top mt-1">
                                    <span className="body-font-bold">
                                        {order.paymetdetails ? 'Amount Paid' : 'To Pay (COD)'}
                                    </span>
                                    <span className="body-font-bold text-success fs-5">
                                        ₹{order.paymetdetails ? order.paymetdetails.amount : finalTotal}
                                    </span>
                                </div>
                                {order.paymetdetails && (
                                    <p className="mb-0 text-success fs-sm text-end body-font-medium mt-1">
                                        <i className="fi fi-ss-check me-1"></i>Paid Online
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Delivery & Contact */}
                        <div className="border rounded-brand p-3 mb-3 bg-white">
                            <h5 className="body-font-semibold mb-3 fs-6">Delivery Details</h5>
                            <div className="row g-3">
                                <div className="col-6">
                                    <p className="mb-1 text-muted fs-sm">Name</p>
                                    <p className="mb-0 body-font-medium">{order.address?.name || '—'}</p>
                                </div>
                                <div className="col-6">
                                    <p className="mb-1 text-muted fs-sm">Phone</p>
                                    <p className="mb-0 body-font-medium">{order.number || '—'}</p>
                                </div>
                                {order.email && (
                                    <div className="col-12">
                                        <p className="mb-1 text-muted fs-sm">Email</p>
                                        <p className="mb-0 body-font-medium">{order.email}</p>
                                    </div>
                                )}
                                <div className="col-12">
                                    <p className="mb-1 text-muted fs-sm">Street Address</p>
                                    <p className="mb-0 body-font-medium lh-sm">{order.address?.address || '—'}</p>
                                </div>
                                <div className="col-6">
                                    <p className="mb-1 text-muted fs-sm">City</p>
                                    <p className="mb-0 body-font-medium">{order.address?.city || '—'}</p>
                                </div>
                                <div className="col-6">
                                    <p className="mb-1 text-muted fs-sm">State</p>
                                    <p className="mb-0 body-font-medium">{order.address?.state || '—'}</p>
                                </div>
                                <div className="col-6">
                                    <p className="mb-1 text-muted fs-sm">Pincode</p>
                                    <p className="mb-0 body-font-medium">{order.address?.pincode || '—'}</p>
                                </div>
                                <div className="col-6">
                                    <p className="mb-1 text-muted fs-sm">Country</p>
                                    <p className="mb-0 body-font-medium">{order.address?.country || 'India'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Cancel button */}
                        {canCancel && (
                            <div className="mb-5">
                                <button className="btn btn-outline-danger w-100 py-2" onClick={cancelOrder}>
                                    Cancel Order
                                </button>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
}

export default Orderdetail;
